import { request } from '@umijs/max';

/** 机器人列表 GET /robot/device/list */
export async function getRobotList(
  payload?: API.PageParams,
): Promise<API.ROBOT.List> {
  return request<API.ListResponse<API.ROBOT.Robot>>('/robot/device/list', {
    method: 'GET',
    params: payload,
  }).then((res) => {
    return res.rows.map((item) => ({
      ...item,
      batteryLevel:
        item.batteryLevel > 1 ? item.batteryLevel : item.batteryLevel * 100,
    }));
  });
}

/** 机器人详情 GET /robot/device/{id} */
export async function getRobotDetail({
  id,
  serialNumber,
}:
  | {
      id: number;
      serialNumber?: string;
    }
  | {
      id?: number;
      serialNumber: string;
    }) {
  return request<API.CommonResponse<API.ROBOT.Robot>>(
    serialNumber
      ? `/robot/device/getBySerialNumber/${serialNumber}`
      : `/robot/device/${id}`,
    {
      method: 'GET',
    },
  ).then((res) => {
    return {
      ...res.data,
      batteryLevel:
        res.data.batteryLevel > 1
          ? res.data.batteryLevel
          : res.data.batteryLevel * 100,
    };
  });
}

/** 更新机器人 POST /robot/device/updateBySerialNumber */
export async function updateRobot(
  serialNumber: string,
  data: Partial<API.ROBOT.Robot> = {},
) {
  return request<API.CommonResponse>('/robot/device/updateBySerialNumber', {
    method: 'POST',
    data: {
      ...data,
      serialNumber,
    },
  });
}

/** 移除机器人 POST /robot/device/register */
export async function removeRobot(serialNumber: string) {
  return request<API.CommonResponse>('/robot/device/register', {
    method: 'POST',
    data: {
      serialNumber,
      registerMode: 'unreg',
    },
  });
}

export const ROBOT_STATUS_MAP: Record<
  API.ROBOT.RobotStatus,
  { color: string; label: string }
> = {
  offline: {
    color: '',
    label: '离线',
  },
  idle: {
    color: 'cyan',
    label: '空闲',
  },
  online: {
    color: 'cyan',
    label: '空闲',
  },
  charging: {
    color: 'green',
    label: '充电中',
  },
  notCharging: {
    color: 'red',
    label: '充电中',
  },
  backToCharge: {
    color: 'orange',
    label: '回桩中',
  },
  backToChargeFailed: {
    color: 'red',
    label: '回桩失败',
  },
  working: {
    color: 'blue',
    label: '工作中',
  },
  error: {
    color: 'red',
    label: '异常',
  },
};

export const CLEAN_MODE_MAP: Record<
  BooleanNumber,
  { color: string; label: string }
> = {
  0: {
    color: 'green',
    label: '开启',
  },
  1: {
    color: '',
    label: '关闭',
  },
};

export const MOTION_STATE_MAP: Record<
  API.ROBOT.MotionState | 'OFFLINE',
  { color: string; label: string; description: string }
> = {
  IDLE: {
    color: 'success',
    label: '空闲',
    description: '当前没有在执行任务',
  },
  NAVIGATING: {
    color: 'processing',
    label: '导航中',
    description: '正在导航',
  },
  STARTING: {
    color: 'orange',
    label: '启动中',
    description: '正在启动巡逻',
  },
  TAKING_PHOTO: {
    color: 'volcano',
    label: '正在拍照',
    description: '正在拍照',
  },
  CLEANING: {
    color: 'cyan',
    label: '清洁中',
    description: '正在清洁',
  },
  LYING_DOWN: {
    color: 'geekblue',
    label: '趴下中',
    description: '正在趴下',
  },
  STANDING_UP: {
    color: 'geekblue',
    label: '站立中',
    description: '正在站立',
  },
  COMPLETED: {
    color: 'success',
    label: '任务完成',
    description: '已完成巡逻任务',
  },
  CANCELLED: {
    color: '',
    label: '任务取消',
    description: '已取消巡逻任务',
  },
  RELOCALIZING: {
    color: 'orange',
    label: '重新定位中',
    description: '正在重新定位',
  },
  CHARGING: {
    color: 'green',
    label: '充电中',
    description: '正在充电',
  },
  GOING_TO_CHARGE: {
    color: 'orange',
    label: '回桩中',
    description: '正在回桩充电',
  },
  LEAVING_CHARGE: {
    color: 'orange',
    label: '离桩中',
    description: '正在离开充电桩',
  },
  NOT_CHARGING: {
    color: 'error',
    label: '未充电',
    description: '机器人在桩上，但未充电',
  },
  CHARGING_NORMAL: {
    color: 'green',
    label: '充电正常',
    description: '充电正常',
  },
  CHARGING_ERROR: {
    color: 'error',
    label: '充电失败',
    description: '充电失败',
  },
  CHARGING_EORROR: {
    color: 'error',
    label: '充电失败',
    description: '充电失败',
  },
  ERROR: {
    color: 'error',
    label: '异常',
    description: '发生错误，巡逻失败',
  },
  OFFLINE: {
    color: '',
    label: '离线',
    description: '机器人不在线',
  },
};

export const ROBOT_TEMPERATURE_STATUS: Record<
  number,
  { color: string; label: string }
> = {
  0: {
    color: 'var(--ant-color-success)',
    label: '正常',
  },
  60: {
    color: 'var(--ant-color-warning)',
    label: '预警',
  },
  100: {
    color: 'var(--ant-color-error)',
    label: '保护',
  },
};

export const ROBOT_JOINTS: {
  label: string;
  key: API.ROBOT.DeviceJoints;
  marker?: { x: number; y: number };
}[] = [
  { label: '侧摆关节', key: 'HipX' },
  { label: '髋关节', key: 'HipY' },
  { label: '膝关节', key: 'Knee' },
  { label: '轮关节', key: 'Wheel' },
];

export const ROBOT_BATTERY_PARTS: {
  label: string;
  key: keyof Pick<
    API.ROBOT.BatteryStatus,
    'batteryTemperatureLeft' | 'batteryTemperatureRight'
  >;
  marker: { x: number; y: number };
}[] = [
  {
    label: '左侧电池',
    key: 'batteryTemperatureLeft',
    marker: { x: 50, y: 67 },
  },
  {
    label: '右侧电池',
    key: 'batteryTemperatureRight',
    marker: { x: 50, y: 40 },
  },
];

export const ROBOT_PARTS: {
  label: string;
  key: API.ROBOT.DeviceParts;
  params: typeof ROBOT_JOINTS;
}[] = [
  {
    label: '左前腿',
    key: 'leftFront',
    params: ROBOT_JOINTS.map((item) => ({
      ...item,
      marker:
        item.key === 'HipX'
          ? {
              x: 33,
              y: 67,
            }
          : item.key === 'HipY'
            ? {
                x: 19,
                y: 68,
              }
            : item.key === 'Knee'
              ? {
                  x: 36,
                  y: 82,
                }
              : item.key === 'Wheel'
                ? {
                    x: 19,
                    y: 85.5,
                  }
                : undefined,
    })),
  },
  {
    label: '右前腿',
    key: 'rightFront',
    params: ROBOT_JOINTS.map((item) => ({
      ...item,
      marker:
        item.key === 'HipX'
          ? {
              x: 33,
              y: 40,
            }
          : item.key === 'HipY'
            ? {
                x: 19,
                y: 39,
              }
            : item.key === 'Knee'
              ? {
                  x: 36,
                  y: 25,
                }
              : item.key === 'Wheel'
                ? {
                    x: 19,
                    y: 21,
                  }
                : undefined,
    })),
  },
  {
    label: '左后腿',
    key: 'leftBack',
    params: ROBOT_JOINTS.map((item) => ({
      ...item,
      marker:
        item.key === 'HipX'
          ? {
              x: 66,
              y: 67,
            }
          : item.key === 'HipY'
            ? {
                x: 82,
                y: 68,
              }
            : item.key === 'Knee'
              ? {
                  x: 64,
                  y: 82,
                }
              : item.key === 'Wheel'
                ? {
                    x: 82,
                    y: 85.5,
                  }
                : undefined,
    })),
  },
  {
    label: '右后腿',
    key: 'rightBack',
    params: ROBOT_JOINTS.map((item) => ({
      ...item,
      marker:
        item.key === 'HipX'
          ? {
              x: 66,
              y: 40,
            }
          : item.key === 'HipY'
            ? {
                x: 82,
                y: 39,
              }
            : item.key === 'Knee'
              ? {
                  x: 64,
                  y: 25,
                }
              : item.key === 'Wheel'
                ? {
                    x: 82,
                    y: 21,
                  }
                : undefined,
    })),
  },
];

export const ROBOT_JOINTS_MOTOR_NORMAL_VALUES: Record<
  keyof API.ROBOT.MotorStatus,
  [number, number]
> = {
  leftFrontHipX: [0, 180],
  leftFrontHipY: [50, 70],
  leftFrontKnee: [0, 180],
  leftFrontWheel: [0, 45],
  rightFrontHipX: [0, 180],
  rightFrontHipY: [50, 70],
  rightFrontKnee: [0, 180],
  rightFrontWheel: [0, 45],
  leftBackHipX: [0, 180],
  leftBackHipY: [50, 70],
  leftBackKnee: [0, 180],
  leftBackWheel: [0, 45],
  rightBackHipX: [0, 180],
  rightBackHipY: [50, 70],
  rightBackKnee: [0, 180],
  rightBackWheel: [0, 45],
};

export const MOCK_ROBOT_STATUS: API.RobotMqttResponse<API.ROBOT.DeviceStatus> =
  {
    payload: {
      batteryStatus: {
        //电池状态信息
        batteryLevelLeft: 50, //左侧电池剩余电量百分比（单位%）
        batteryLevelRight: 50, //右侧电池剩余电量百分比
        batteryTemperatureLeft: 27.799999237060547, //左侧电池温度（单位℃）
        batteryTemperatureRight: 27.799999237060547, //右侧电池温度
        chargeLeft: false, //左侧电池充电状态（true=充电中，false=未在充电）
        chargeRight: false, //右侧电池充电状态（true=充电中，false=未在充电）
        voltageLeft: 78, //左侧电池电压（单位V）
        voltageRight: 78, //右侧电池电压（单位V）
      },
      deviceTemperature: {
        //各关节电机和驱动器的温度信息
        leftBackHipXDriver: 52.13999938964844, //左后侧摆关节电机驱动器温度
        leftBackHipXMotor: 37.90999984741211, //左后侧摆关节电机温度
        leftBackHipYDriver: 47.31999969482422, //左后髋关节电机驱动温度
        leftBackHipYMotor: 32.7400016784668, //左后髋关节电机温度
        leftBackKneeDriver: 51.5099983215332, //左后膝关节电机驱动温度
        leftBackKneeMotor: 36.52000045776367, //左后膝关节电机温度
        leftBackWheelDriver: 46.77000045776367, //左后足轮电机驱动温度
        leftBackWheelMotor: 32.06999969482422, //左后足轮电机温度
        leftFrontHipXDriver: 55.36000061035156,
        leftFrontHipXMotor: 41.54999923706055,
        leftFrontHipYDriver: 46.380001068115234,
        leftFrontHipYMotor: 32.79999923706055,
        leftFrontKneeDriver: 52.189998626708984,
        leftFrontKneeMotor: 37.68000030517578,
        leftFrontWheelDriver: 44.11000061035156,
        leftFrontWheelMotor: 30.670000076293945,
        rightBackHipXDriver: 50.90999984741211,
        rightBackHipXMotor: 39.02000045776367,
        rightBackHipYDriver: 47.709999084472656,
        rightBackHipYMotor: 31.940000534057617,
        rightBackKneeDriver: 49.209999084472656,
        rightBackKneeMotor: 35.209999084472656,
        rightBackWheelDriver: 45.34000015258789,
        rightBackWheelMotor: 31.489999771118164,
        rightFrontHipXDriver: 51.72999954223633,
        rightFrontHipXMotor: 37.63999938964844,
        rightFrontHipYDriver: 47.04999923706055,
        rightFrontHipYMotor: 32.70000076293945,
        rightFrontKneeDriver: 55.779998779296875,
        rightFrontKneeMotor: 42.119998931884766,
        rightFrontWheelDriver: 44.88999938964844,
        rightFrontWheelMotor: 31.770000457763672,
      },
      led: {
        fill: {
          back: 0, //后照明灯状态（关闭=0，开启=1）
          front: 0, //前照明灯状态（关闭=0，开启=1）
        },
      },
    },
    serialNumber: 'SN269',
    timestamp: 1761364037294,
    type: '/device_status',
  };

export const MOCK_MOTION_STATUS: API.RobotMqttResponse<API.ROBOT.DeviceMotion> =
  {
    payload: {
      motionStatus: {
        height: 0.12961743772029877,
        linearX: 0.0,
        linearY: 0.0,
        omegaZ: -0.0018325434066355228,
        payload: 0.0,
        pitch: -0.007476466707885265,
        remainMile: 13.260000228881836,
        roll: 0.012434565462172031,
        yaw: -0.22337494790554047,
      },
      motorStatus: {
        leftFrontHipX: 0.0, //左前腿侧摆关节角度
        leftFrontHipY: 0.0, //左前腿髋关节角度
        leftFrontKnee: 0.0, //左前腿膝关节角度
        leftFrontWheel: 0.0, //左前腿足轮转速
        rightFrontHipX: 0.0, //右前腿侧摆关节角度
        rightFrontHipY: 0.0, //右前腿髋关节角度
        rightFrontKnee: 0.0, //右前腿膝关节角度
        rightFrontWheel: 0.0, //右前腿足轮转速
        leftBackHipX: 0.0, //左后腿侧摆关节角度
        leftBackHipY: 0.0, //左后腿髋关节角度
        leftBackKnee: 0.0, //左后腿膝关节角度
        leftBackWheel: 0.0, //左后腿足轮转速
        rightBackHipX: 0.0, //右后腿侧摆关节角度
        rightBackHipY: 0.0, //右后腿髋关节角度
        rightBackKnee: 0.0, //右后腿膝关节角度
        rightBackWheel: 0.0, //右后腿足轮转速
      },
    },
    serialNumber: 'SN268',
    timestamp: 1761364037294,
    type: '/motion_status',
  };
export const mockRobotStatus = () => {
  MOCK_ROBOT_STATUS.payload.batteryStatus.batteryLevelLeft =
    Math.random() * 100;
  MOCK_ROBOT_STATUS.payload.batteryStatus.batteryLevelRight =
    Math.random() * 100;
  Object.keys(MOCK_ROBOT_STATUS.payload.deviceTemperature).forEach((key) => {
    MOCK_ROBOT_STATUS.payload.deviceTemperature[
      key as keyof API.ROBOT.DeviceTemperature
    ] = Math.random() * 100;
  });
  if (MOCK_MOTION_STATUS.payload.motorStatus) {
    Object.keys(MOCK_MOTION_STATUS.payload.motorStatus).forEach((key) => {
      if (MOCK_MOTION_STATUS.payload.motorStatus) {
        MOCK_MOTION_STATUS.payload.motorStatus[
          key as keyof API.ROBOT.MotorStatus
        ] = 50 + Math.random() * 30;
      }
    });
  }
  MOCK_MOTION_STATUS.payload.motionStatus.linearX = Number(
    (15 + Math.random() * 3).toFixed(1),
  );
  MOCK_MOTION_STATUS.payload.motionStatus.yaw = Math.random();
  Object.keys(MOCK_ROBOT_STATUS.payload.led.fill).forEach((key) => {
    MOCK_ROBOT_STATUS.payload.led.fill[
      key as keyof API.ROBOT.LedStatus['fill']
    ] = Math.random() > 0.5 ? 1 : 0;
  });
  return MOCK_ROBOT_STATUS;
};

export const MOCK_ROBOT_TASK: API.RobotMqttResponse<API.ROBOT.DeviceTask> = {
  payload: {
    error: '',
    goal: {
      //当前目标点信息（前端暂时不用）
      gait: 0,
      manner: 0,
      navMode: 0,
      obsMode: 0,
      pitch: 0,
      pointType: 0,
      posX: 0,
      posY: 0,
      posZ: 0,
      roll: 0,
      speedLevel: 0,
      tasks: 0,
      yaw: 0,
    },
    pose: {
      //机器人当前位置和姿态
      orientation: {
        //姿态坐标
        w: 0.9992907244169459,
        x: 0,
        y: 0,
        z: 0.03765697935649639,
      },
      position: {
        //位置坐标
        x: -0.012084691785275936,
        y: 0.3437346816062927,
        z: -0.39096492528915405,
      },
    },
    state: 'NAVIGATING' as API.ROBOT.MotionState, //当前巡逻状态
    taskId: 0, //当前任务ID
  },
  serialNumber: 'SN269',
  timestamp: 1761364035294,
  type: '/patrol_status',
};
export const mockRobotTask = () => {
  const { orientation, rpy, position } = MOCK_ROBOT_TASK.payload.pose;
  if (orientation) {
    Object.keys(orientation).forEach((key) => {
      orientation[key as keyof API.ROBOT.DevicePose['orientation']] =
        Math.random() as never;
    });
  }
  if (rpy) {
    Object.keys(rpy).forEach((key) => {
      rpy[key as keyof API.ROBOT.DevicePose['rpy']] = Math.random() as never;
    });
  }
  Object.keys(position).forEach((key) => {
    position[key as keyof API.ROBOT.DevicePose['position']] = Math.random();
  });
  return MOCK_ROBOT_TASK;
};
